<div class="esign-setup-page container-fluid px-2 px-md-3 py-3">
  <div class="d-flex flex-column gap-3">
    <section class="esign-setup-hero rounded-4 overflow-hidden shadow-sm">
      <div class="esign-setup-hero-surface p-3 p-md-4 p-xl-4">
        <div class="row g-3 align-items-stretch">
          <div class="col-12">
            <div class="d-flex flex-column h-100">
              <h5 class="text-white fw-bold lh-sm mb-2 esign-setup-title">
                {{ template_display_name }}
              </h5>
              <p class="text-white-50 mb-4 esign-setup-subtitle">
                Assign the right template form, connect the signing workflow, and prepare the document for Docuseal tagging from one responsive setup screen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-white rounded-4 border shadow-sm p-2">
      <div class="d-flex flex-wrap gap-2">
        <button
          v-for="tab in setup_tabs"
          :key="tab.key"
          type="button"
          class="btn rounded-4 px-3 px-md-4 py-2 fw-semibold esign-tab-btn"
          :class="setup_active_tab === tab.key ? 'active' : ''"
          @click="set_active_tab(tab.key)"
        >
          <i :class="['bi', tab.icon, 'me-2']"></i>
          {{ tab.label }}
        </button>
      </div>
    </section>

    <section v-if="setup_active_tab === 'forms'" class="row g-3">
      <div class="col-12 col-xl-8">
        <div class="bg-white rounded-4 border shadow-sm p-3 p-md-4">
          <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
            <div>
              <h2 class="fw-bold fs-5 mb-1">Assign Template Forms</h2>
              <p class="text-secondary mb-0 text-xs">
                Pull all template forms into this view and attach the one that should drive this template setup.
              </p>
            </div>
          </div>

          <div v-if="forms_loading" class="text-center text-secondary p-4">
            <div class="fw-semibold text-x mb-1">Loading template forms...</div>
          </div>

          <div v-else-if="!template_forms.length" class="text-center text-secondary p-4">
            <div class="fw-semibold text-x mb-1">No template forms found</div>
            <div class="text-xs">
              Created template forms will appear here once they are available.
            </div>
          </div>

          <div v-else class="d-flex flex-column gap-3">
            <article
              v-for="form in template_forms"
              :key="form.id"
              class="esign-selection-card"
              :class="selected_form_id === form.id ? 'is-selected' : ''"
            >
              <div class="row g-3 align-items-center">
                <div class="col-12 col-lg-8">
                  <div class="d-flex align-items-start gap-3">
                    <div class="esign-selection-icon">
                      <i class="bi bi-ui-checks-grid"></i>
                    </div>
                    <div class="min-w-0">
                      <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h3 class="fw-bold fs-6 mb-0">{{ form.name }}</h3>
                      </div>
                      <p class="text-secondary text-xs mb-2">{{ form.description }}</p>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-lg-4">
                  <div class="d-flex flex-column flex-sm-row flex-lg-column gap-2 align-items-stretch align-items-lg-end">
                    <button
                      type="button"
                      class="btn btn-sm rounded-4 px-3 py-2 fw-semibold esign-assign-btn"
                      :class="is_form_assigned_to_template(form.id) ? 'is-selected' : ''"
                      :disabled="Boolean(assigning_form_id)"
                      @click="assign_template_form_to_template(form.id)"
                    >
                      {{
                        assigning_form_id === form.id
                          ? (is_form_assigned_to_template(form.id) ? 'Unassigning...' : 'Assigning...')
                          : is_form_assigned_to_template(form.id)
                            ? 'Unassign from Template'
                            : 'Assign to Template'
                      }}
                    </button>
                    <div class="text-secondary text-xs text-lg-end">
                      Links onboarding form:
                      <span class="fw-semibold text-dark">{{ form.onboarding_form }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-4">
        <div class="d-flex flex-column gap-3 h-100">
          <div class="esign-progress-panel esign-progress-panel-sidebar">
            <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
              <div>
                <div class="text-uppercase small text-white-50 mb-1">Setup progress</div>
                <div class="text-white fw-bold fs-5">{{ completion_ratio }} completed</div>
              </div>
              <div class="esign-progress-ring">
                <span>{{ completion_ratio }}</span>
              </div>
            </div>

            <div class="d-flex flex-column gap-2">
              <article
                v-for="item in setup_progress"
                :key="item.title"
                class="esign-progress-item"
                :class="item.complete ? 'is-complete' : ''"
              >
                <div class="d-flex align-items-start gap-3">
                  <div class="esign-progress-icon">
                    <i :class="item.complete ? 'bi bi-check2' : 'bi bi-dot'"></i>
                  </div>
                  <div class="min-w-0">
                    <div class="fw-semibold text-white">{{ item.title }}</div>
                    <div class="text-white-50 text-xs">{{ item.detail }}</div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-else-if="setup_active_tab === 'workflows'" class="row g-3">
      <div class="col-12 col-xl-8">
        <div class="bg-white rounded-4 border shadow-sm p-3 p-md-4">
          <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
            <div>
              <h2 class="fw-bold fs-5 mb-1">Assign Workflow</h2>
              <p class="text-secondary mb-0 text-xs">
                Pull all e-signing workflows into this page and choose the one that should control the signer path for this template.
              </p>
            </div>
            <div class="esign-inline-note">
              Select one workflow
            </div>
          </div>

          <div v-if="workflows_loading" class="text-center text-secondary p-4">
            <div class="fw-semibold text-x mb-1">Loading workflows...</div>
          </div>

          <div v-else-if="!workflows.length" class="text-center text-secondary p-4">
            <div class="fw-semibold text-x mb-1">No workflows found</div>
            <div class="text-xs">
              Created workflows will appear here once they are available.
            </div>
          </div>

          <div v-else class="d-flex flex-column gap-3">
            <article
              v-for="workflow in workflows"
              :key="workflow.id"
              class="esign-selection-card"
              :class="selected_workflow_id === workflow.id ? 'is-selected' : ''"
            >
              <div class="row g-3 align-items-center">
                <div class="col-12 col-lg-8">
                  <div class="d-flex align-items-start gap-3">
                    <div class="esign-selection-icon workflow">
                      <i class="bi bi-diagram-3"></i>
                    </div>
                    <div class="min-w-0 w-100">
                      <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h3 class="fw-bold fs-6 mb-0">{{ workflow.name }}</h3>
                      </div>
                      <p class="text-secondary text-xs mb-3">{{ workflow.description || workflow.entity_type }}</p>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-lg-4">
                  <div class="d-flex flex-column flex-sm-row flex-lg-column gap-2 align-items-stretch align-items-lg-end">
                    <button
                      type="button"
                      class="btn btn-sm rounded-4 px-3 py-2 fw-semibold esign-assign-btn"
                      :class="is_workflow_assigned_to_template(workflow.id) ? 'is-selected' : ''"
                      :disabled="Boolean(assigning_workflow_id)"
                      @click="assign_workflow(workflow.id)"
                    >
                      {{
                        assigning_workflow_id === workflow.id
                          ? 'Assigning...'
                          : is_workflow_assigned_to_template(workflow.id)
                            ? 'Assigned to Template'
                            : 'Assign Workflow'
                      }}
                    </button>
                    <div class="text-secondary text-xs text-lg-end">
                      {{ workflow_step_summary(workflow) }} · Updated {{ format_date(workflow.created_at) }}
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-4">
        <div class="d-flex flex-column gap-3 h-100">
          <div class="esign-progress-panel esign-progress-panel-sidebar">
            <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
              <div>
                <div class="text-uppercase small text-white-50 mb-1">Setup progress</div>
                <div class="text-white fw-bold fs-5">{{ completion_ratio }} completed</div>
              </div>
              <div class="esign-progress-ring">
                <span>{{ completion_ratio }}</span>
              </div>
            </div>

            <div class="d-flex flex-column gap-2">
              <article
                v-for="item in setup_progress"
                :key="item.title"
                class="esign-progress-item"
                :class="item.complete ? 'is-complete' : ''"
              >
                <div class="d-flex align-items-start gap-3">
                  <div class="esign-progress-icon">
                    <i :class="item.complete ? 'bi bi-check2' : 'bi bi-dot'"></i>
                  </div>
                  <div class="min-w-0">
                    <div class="fw-semibold text-white">{{ item.title }}</div>
                    <div class="text-white-50 text-xs">{{ item.detail }}</div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-else class="row g-3">
      <div class="col-12 col-xl-8">
        <ConfigureDocusealTemplate
  :key="`${template_id}-${assigned_form_id}-${assigned_workflow_id}`"
/>
      </div>

      <div class="col-12 col-xl-4">
        <div class="esign-progress-panel esign-progress-panel-sidebar h-100">
          <div class="d-flex align-items-start justify-content-between gap-3 mb-3">
            <div>
              <div class="text-uppercase small text-white-50 mb-1">Setup progress</div>
              <div class="text-white fw-bold fs-5">{{ completion_ratio }} completed</div>
            </div>
            <div class="esign-progress-ring">
              <span>{{ completion_ratio }}</span>
            </div>
          </div>

          <div class="d-flex flex-column gap-2">
            <article
              v-for="item in setup_progress"
              :key="item.title"
              class="esign-progress-item"
              :class="item.complete ? 'is-complete' : ''"
            >
              <div class="d-flex align-items-start gap-3">
                <div class="esign-progress-icon">
                  <i :class="item.complete ? 'bi bi-check2' : 'bi bi-dot'"></i>
                </div>
                <div class="min-w-0">
                  <div class="fw-semibold text-white">{{ item.title }}</div>
                  <div class="text-white-50 text-xs">{{ item.detail }}</div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  </div>
</div>
